import React, { useMemo, useEffect } from 'react';

// Pagination for the Locations lists (sites and people).
//
// Both lists are long enough that scrolling one is a poor way to find a row,
// but they already have search and filters — so paging sits *after* filtering:
// you narrow with the controls, then page through what is left. Searching
// therefore looks at every site/person, not just the ten currently on screen,
// which is the behaviour people expect from a search box.
export const PAGE_SIZE = 10;

// Page numbers to render, collapsing long runs to an ellipsis so the control
// stays one line: 1 … 4 5 6 … 12. Neighbours either side of the current page
// plus the two ends are always shown.
function pageItems(page, pageCount) {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const out = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(pageCount - 1, page + 1);
  if (from > 2) out.push('…');
  for (let p = from; p <= to; p++) out.push(p);
  if (to < pageCount - 1) out.push('…');
  out.push(pageCount);
  return out;
}

// Derives the current slice and keeps `page` valid.
//
// The clamp matters: filtering down while on page 4 would otherwise leave you
// on a page that no longer exists, showing an empty list that looks like "no
// results" rather than the rows that do match.
export function usePaged(items, page, setPage, pageSize = PAGE_SIZE) {
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount, setPage]);

  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * pageSize;
  const pageItemsSlice = useMemo(
    () => items.slice(start, start + pageSize),
    [items, start, pageSize],
  );

  return {
    paged: pageItemsSlice,
    page: safePage,
    pageCount,
    // 1-based inclusive range of what is on screen, for the footer summary.
    from: items.length === 0 ? 0 : start + 1,
    to: Math.min(start + pageSize, items.length),
  };
}

const btnBase = {
  minWidth: 26,
  height: 26,
  padding: '0 7px',
  borderRadius: 6,
  border: '1px solid var(--border-default)',
  background: 'var(--surface-default)',
  color: 'var(--text-primary)',
  fontSize: 12,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

function Arrow({ dir }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points={dir === 'prev' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'} />
    </svg>
  );
}

// Nothing is rendered for a single page — a pager under ten rows is noise.
function Pager({ page, pageCount, setPage, label = 'results' }) {
  if (pageCount <= 1) return null;

  const go = p => setPage(Math.min(pageCount, Math.max(1, p)));

  return (
    <nav aria-label={`${label} pagination`} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <button type="button" aria-label="Previous page" disabled={page === 1}
        onClick={() => go(page - 1)}
        style={{ ...btnBase, opacity: page === 1 ? 0.4 : 1, cursor: page === 1 ? 'default' : 'pointer' }}>
        <Arrow dir="prev" />
      </button>

      {pageItems(page, pageCount).map((p, i) =>
        p === '…' ? (
          <span key={`gap${i}`} aria-hidden="true"
            style={{ padding: '0 2px', fontSize: 12, color: 'var(--text-subtle)' }}>…</span>
        ) : (
          <button key={p} type="button" onClick={() => go(p)}
            aria-label={`Page ${p}`}
            aria-current={p === page ? 'page' : undefined}
            style={{
              ...btnBase,
              fontWeight: p === page ? 600 : 400,
              background: p === page ? 'var(--brand-default)' : 'var(--surface-default)',
              borderColor: p === page ? 'var(--brand-default)' : 'var(--border-default)',
              color: p === page ? '#fff' : 'var(--text-primary)',
            }}>
            {p}
          </button>
        ),
      )}

      <button type="button" aria-label="Next page" disabled={page === pageCount}
        onClick={() => go(page + 1)}
        style={{ ...btnBase, opacity: page === pageCount ? 0.4 : 1, cursor: page === pageCount ? 'default' : 'pointer' }}>
        <Arrow dir="next" />
      </button>
    </nav>
  );
}

export default Pager;
